// /work/ index — editorial mosaic redesign (2026-08-21, per explicit
// request), replacing the old uniform 3-column .rel-grid/.rel-card layout.
// Two complaints drove this: (1) the old cards defaulted to a near-black
// resting background (.rel-card{background:var(--night)}) that read as
// heavy/dark for a page meant to showcase light, airy interiors — user's
// own words, "no black background"; (2) the page felt like it "ended too
// fast" after the grid, straight into the CTA, with nothing else on it.
//
// Pulls straight from PROJECTS, so a new project shows up automatically —
// tile SIZE is assigned by cycling through MOSAIC_SIZE_CLASSES (index %
// length), not hardcoded per slug, so this still works correctly if a
// 7th+ project is ever added, just repeating the same big/medium/medium/
// small/small/small editorial rhythm every 6.
// Shopify note: flattened to project-{slug}-{base}-{width}.jpg, served
// from window.SHOPIFY_ASSET_BASE — see the same note in project-template.js.
function workImgTag(slug, image, sizesOverride) {
  const base = `${window.SHOPIFY_ASSET_BASE}project-${slug}-`;
  const widths = image.widths;
  const largest = widths[widths.length - 1];
  const srcset = widths.map((w) => `${base}${image.base}-${w}.jpg ${w}w`).join(",\n            ");
  return `<img
      src="${base}${image.base}-${largest}.jpg"
      srcset="${srcset}"
      sizes="${sizesOverride || "(min-width: 821px) 33vw, 100vw"}"
      loading="lazy"
      alt="${image.alt}">`;
}

function workCardSubtitle(project) {
  if (project.kicker) return project.kicker;
  const when = project.date || project.year;
  return when ? `${project.location} · ${when}` : project.location;
}

// Cycled by index, not hand-assigned per slug — see file-top comment.
const MOSAIC_SIZE_CLASSES = [
  "work-mosaic-tile--lg",
  "work-mosaic-tile--md",
  "work-mosaic-tile--md",
  "work-mosaic-tile--sm",
  "work-mosaic-tile--sm",
  "work-mosaic-tile--sm",
];

function renderWorkIndex(root) {
  const projects = Object.values(PROJECTS);

  const tiles = projects.map((p, i) => {
    const sizeClass = MOSAIC_SIZE_CLASSES[i % MOSAIC_SIZE_CLASSES.length];
    const num = String(i + 1).padStart(2, "0");
    return `
    <a class="work-mosaic-tile ${sizeClass} reveal-el" href="/pages/${p.slug}">
      ${workImgTag(p.slug, p.cardImage || p.heroImage, sizeClass === "work-mosaic-tile--lg" ? "100vw" : "(min-width: 821px) 50vw, 100vw")}
      <div class="work-mosaic-info">
        <div class="work-mosaic-text">
          <span class="work-mosaic-label">${p.title}</span>
          <span class="work-mosaic-loc">${workCardSubtitle(p)}</span>
        </div>
        <span class="work-mosaic-num">${num}</span>
      </div>
    </a>`;
  }).join("");

  // Two full-bleed "showcase" moments (each project's own heroImage)
  // were tried here 2026-08-21, then removed the same day per explicit
  // follow-up: they weren't clickable (a real UX gap — plain divs, not
  // links) and, more fundamentally, showing a second photo of a project
  // right after its own mosaic tile read as repetitive to the user even
  // though the specific photo differed (heroImage vs cardImage) — the
  // *project* was still doubling up, not just the image. The lead
  // paragraph and closing section below (the other two "page ends too
  // fast" additions from that same pass) were kept; only the showcase
  // images were cut.

  root.innerHTML = `
    <section class="section-wrap">
      <div class="section-label">The Work</div>
      <h1 class="section-title">Every project, read closely.</h1>
      <p class="section-lead reveal-el">Every space we've touched, read the same way we design them — closely, and one room at a time.</p>
    </section>
    <div class="section-wrap work-mosaic">${tiles}</div>
    <section class="section-wrap work-closing">
      <h2 class="work-closing-title reveal-el">Become the next project.</h2>
      <p class="work-closing-lead reveal-el">If a room here felt like something you'd want to live inside, that feeling is the whole brief. Reach out, and let's start reading yours.</p>
    </section>
    <div class="work-index-cta-fade" id="work-index-cta-fade" aria-hidden="true"></div>
  `;
  document.title = "The Work — Sixth Sense Studio";
}

// ---- Work index -> CTA wash — same scroll-scrubbed fade technique as the
// homepage's Process -> Meliza / Reflections -> CTA washes (motion.js),
// reimplemented here since this page doesn't load motion.js. Requires bare
// GSAP + ScrollTrigger (see the <script> tags in work/index.html). Fades
// #work-index-cta-fade from 0->1 opacity as .work-index's bottom edge
// approaches the viewport top, so the drop into the CTA band reads as a
// gradual reveal instead of a hard light->dark cut.
function initWorkIndexCtaFade(root) {
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (reducedMotion || !window.gsap || !window.ScrollTrigger) return;
  const fade = root.querySelector("#work-index-cta-fade");
  if (!fade) return;
  gsap.registerPlugin(ScrollTrigger);
  gsap.to(fade, {
    opacity: 1,
    ease: "none",
    // end offset: percentage-of-viewport, same fix as project-template.js's
    // own CTA fade (2026-08-21, superseding an earlier flat-240px attempt
    // that didn't scale to real monitor heights) — see that file's
    // comment for the full reasoning.
    scrollTrigger: {
      trigger: root,
      start: "bottom bottom",
      end: () => "bottom top+=" + Math.round(window.innerHeight * 0.4),
      scrub: true,
    },
  });
}

// Synchronous at script-parse time — reveal.js queries .reveal-el the
// instant it runs, same rule as every other renderer in this project.
const workIndexRoot = document.getElementById("work-index");
renderWorkIndex(workIndexRoot);
initWorkIndexCtaFade(workIndexRoot);
